import { Form, FormikProps } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { QcsMenuItem } from '../common/basic/QcsMenuItem';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { Input } from '../common/form/Input';
import { Select } from '../common/form/Select';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { QcsTypography } from '../common/basic/QcsTypography';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { CancelToken } from 'axios';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { Grid } from '../common/grid/Grid';
import { employeeActions, getEmployee, selectEmployee, selectEmployeeState } from '../../store/entities/employee';
import { employeeListActions } from '../../store/entities/employeeList';
import { QcsFormHelperText } from '../common/basic/QcsFormHelperText';
import { QcsButton } from '../common/basic/QcsButton';
import styled from '@emotion/styled';
import { CreateUserModal } from './CreateUserModal';
import { EstablishmentFilter } from '../establishment/EstablishmentFilter';
import { getCompanyChecks, selectCompanyChecksCanCreateUser, selectCompanyChecksState } from '../../store/entities/companyChecks';
import { userListActions } from '../../store/entities/userList';
import { PhoneInput } from '../common/form/PhoneInput';
import { getFirstLastNameObj, getLangNameObj } from '../../utils/format';
import QcsAlert from '../common/basic/QcsAlert';

import { QcsBox } from '../common/basic/QcsBox';
import { QcsDialog } from '../common/basic/QcsDialog';
import { QcsDialogActions } from '../common/basic/QcsDialogActions';
import { QcsDialogContentText } from '../common/basic/QcsDialogContentText';
import { DialogContent, DialogTitle } from '@mui/material';
import { SubmitAndNewButtons } from '../common/form/SubmitAndNewButtons';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { allEstablishmentListActions, getAllEstablishmentList, selectAllEstablishmentList } from '../../store/entities/allEstablishmentList';
import { EmployeeDto } from '@api/models/employees/EmployeeDto';
import { EstablishmentDto } from '@api/models/establishment/EstablishmentDto';
import { EntityState } from '@api/models/enums/EntityState';
import { invitationListActions } from '../../store/entities/invitationList';
import { visitationListActions } from '../../store/entities/visitationList';
import { entryRequestListActions } from '../../store/entities/entryRequestList';
import { workplaceListActions } from '../../store/entities/workplaceList';
import { establishmentListActions } from '../../store/entities/establishmentList';
import { employeeService } from '@api/services/services';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';

interface EmployeeDetailForm extends EmployeeDto {
  saveAndNew?: boolean;
}

const UserButton = styled(QcsButton)(() => ({
  width: 'fit-content',
}));

export const EmployeeDetail: FC = () => {
  const { t, i18n } = useTranslation();
  const { goBack, navigate, getBackPath } = useNavigation('/employee');
  const { employeeId } = useParams();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const employee = useAppSelector(selectEmployee);
  const establishmentList = useAppSelector(selectAllEstablishmentList);
  const companyChecksCanCreateUser = useAppSelector(selectCompanyChecksCanCreateUser);
  const dispatch = useAppDispatch();
  const [creatingUser, setCreatingUser] = useState(false);
  const [pendingSyncDecision, setPendingSyncDecision] = useState<EmployeeDetailForm | null>(null);
  const formikRef = useRef<FormikProps<EmployeeDetailForm> | null>(null);

  useEffect(() => {
    if (employeeId === 'new') {
      dispatch(employeeActions.default());
    } else {
      dispatch(getEmployee(employeeId!));
      dispatch(getCompanyChecks());
    }
  }, [employeeId, identityCompanyId, dispatch]);

  const finalizeSubmit = async (data: EmployeeDetailForm, syncUserState: boolean) => {
    if (employeeId === 'new') {
      await employeeService.create(data);
    } else {
      await employeeService.update(employeeId!, data, { syncUserState });
    }

    dispatch(employeeListActions.reload(true));
    dispatch(userListActions.reload(true));
    dispatch(invitationListActions.reload(true));
    dispatch(visitationListActions.reload(true));
    dispatch(entryRequestListActions.reload(true));
    dispatch(workplaceListActions.reload(true));
    dispatch(establishmentListActions.reload(true));

    if (data.saveAndNew) {
      formikRef.current?.resetForm();
    } else {
      goBack();
    }
  };

  const handleSubmit = async (data: EmployeeDetailForm) => {
    const isReactivation =
      !!employee?.user && employee?.state === EntityState.Inactive && data.state === EntityState.Active;

    if (isReactivation) {
      setPendingSyncDecision(data);
      return;
    }

    await finalizeSubmit(data, false);
  };

  const handleSyncDecision = async (syncUserState: boolean) => {
    const data = pendingSyncDecision;
    setPendingSyncDecision(null);
    if (data) {
      await finalizeSubmit(data, syncUserState);
    }
  };

  const handleReloadData = () => {
    dispatch(employeeListActions.reload(true));
    dispatch(userListActions.reload(true));
  };

  const handleEstablishmentGetData = (cancelToken: CancelToken) => {
    dispatch(getAllEstablishmentList(cancelToken));
  };

  const handleIsRowDisabled = (item: EstablishmentDto) => item.state !== EntityState.Active;

  const handleEstablishmentRenderData = (item: EstablishmentDto) => {
    return (
      <>
        <QcsTableCell>{getLangNameObj(i18n, item)}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{getFirstLastNameObj(item.contact)}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{item.contact.email}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{item.contact.phone}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{t('entityState.' + item.state)}</QcsTableCell>
      </>
    );
  };

  const handleCreateUser = () => {
    setCreatingUser(true);
  };

  const handleCloseCreatingUser = () => {
    setCreatingUser(false);
  };

  const handleGoToUser = (userId: string) => {
    navigate(PathKey.UserDetail, { userId }, { backTo: `/employee/${employeeId}` });
  };

  return (
    <>
      <CreateUserModal open={creatingUser} onClose={handleCloseCreatingUser} reloadData={handleReloadData} />

      <QcsDialog open={!!pendingSyncDecision} onClose={() => handleSyncDecision(false)}>
        <DialogTitle>{t('employee.syncUserStateDialog.title')}</DialogTitle>
        <DialogContent>
          <QcsDialogContentText>{t('employee.syncUserStateDialog.message')}</QcsDialogContentText>
        </DialogContent>
        <QcsDialogActions>
          <QcsButton onClick={() => handleSyncDecision(false)}>{t('employee.syncUserStateDialog.no')}</QcsButton>
          <QcsButton variant="contained" onClick={() => handleSyncDecision(true)}>
            {t('employee.syncUserStateDialog.yes')}
          </QcsButton>
        </QcsDialogActions>
      </QcsDialog>

      <DetailHeader title="employee.detailTitle" backTo={getBackPath} />
      {establishmentList.data?.content.length === 0 && (
        <QcsBox sx={{ pb: '1rem' }}>
          <QcsAlert severity="warning">{t('user.establishmentRequired')}</QcsAlert>
        </QcsBox>
      )}
      <ShowResourceContent
        resource={resources.getEmployee}
        fetchStateSelector={employeeId === 'new' ? selectEmployeeState : [selectEmployeeState, selectCompanyChecksState]}
      >
        <FormikDebounce<EmployeeDetailForm>
          initialValues={employee}
          validationSchema={Yup.object().shape({
            name: validations.stringRequired(t),
            lastName: validations.stringRequired(t),
            phone: validations.phoneOptional(t),
            email: validations.emailRequired(t),
            establishments: validations.arrayNotEmpty(t),
          })}
          onSubmit={handleSubmit}
          innerRef={formikRef}
          enableReinitialize
        >
          {({ values, touched, errors, setFieldValue, setFieldTouched }) => (
            <Form>
              <FormContainer>
                {employeeId !== 'new' && !values.user && (
                  <UserButton variant="contained" onClick={handleCreateUser} disabled={!companyChecksCanCreateUser}>
                    {t('employee.createUser')}
                  </UserButton>
                )}
                {values.user && (
                  <UserButton variant="contained" onClick={() => handleGoToUser(values.user!)}>
                    {t('employee.goToUser')}
                  </UserButton>
                )}
                <Input name="name" label={t('employee.name')} maxLength={100} required />
                <Input name="lastName" label={t('employee.lastName')} maxLength={100} required />
                <PhoneInput name="phone" label={t('employee.phone')} />
                <Input name="email" removeSpaces label={t('employee.email')} maxLength={100} required />

                <div>
                  <QcsTypography variant="h6">{t('employee.detailEstablishmentSelect')}:</QcsTypography>
                  <Grid<EstablishmentDto>
                    headers={[
                      { captionStr: 'establishment.name', orderName: 'name' },
                      {
                        captionStr: 'establishment.contactName',
                        orderName: 'contact.name',
                        hideOnMobile: true,
                      },
                      {
                        captionStr: 'establishment.contactEmail',
                        orderName: 'contact.email',
                        hideOnMobile: true,
                      },
                      {
                        captionStr: 'establishment.contactPhone',
                        orderName: 'contact.phone',
                        hideOnMobile: true,
                      },
                      {
                        captionStr: 'establishment.state',
                        orderName: 'state',
                        hideOnMobile: true,
                      },
                    ]}
                    //establishmentList is used by Establishment.tsx - must have same filter (actual) or split store
                    data={establishmentList}
                    gridActions={allEstablishmentListActions}
                    renderData={handleEstablishmentRenderData}
                    isRowDisabled={handleIsRowDisabled}
                    getData={handleEstablishmentGetData}
                    checkbox={true}
                    checkedItems={values.establishments}
                    changeChecked={(items) => {
                      setFieldTouched('establishments', true);
                      setFieldValue('establishments', items);
                    }}
                    filter={<EstablishmentFilter query={establishmentList.query} />}
                  />
                  {touched.establishments && !!errors.establishments && <QcsFormHelperText error={true}>{errors.establishments}</QcsFormHelperText>}
                </div>

                <Input name="office" label={t('employee.office')} maxLength={100} />
                <Input name="jobPosition" label={t('employee.jobPosition')} maxLength={100} />
                <Select name="state" label={t('employee.state')}>
                  <QcsMenuItem value={EntityState.Active}>{t('entityState.ACTIVE')}</QcsMenuItem>
                  <QcsMenuItem value={EntityState.Inactive}>{t('entityState.INACTIVE')}</QcsMenuItem>
                </Select>
                <Input name="note" label={t('employee.note')} maxLength={250} multiline={true} rows={4} />
                <SubmitAndNewButtons id={employeeId} errorResource={[resources.createEmployee, resources.updateEmployee]} />
              </FormContainer>
            </Form>
          )}
        </FormikDebounce>
      </ShowResourceContent>
    </>
  );
};
